import React, { useCallback, useContext, useState, useMemo, useEffect } from 'react';
import _ from 'lodash';
import { withViewModelService } from '@xengage/gw-portals-viewmodel-react';
import { wizardProps } from 'wmic-pe-portals-custom-wizard-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { WMICWizardChangeOrRenewalPage, useWizardModals, useDocumentTitle } from 'wmic-pe-portals-wizard-components-ui';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { WMICUserAccessUtil, WMICConstantsUtil, WMICLogger } from 'wmic-pe-portals-utils-js';
import { WMICPolicyDetailsComponent } from 'wmic-pe-capability-gateway-common-react';
import { messages as commonMessages } from 'wmic-pe-capability-gateway-policyrenewal-common-react';
import { TranslatorContext } from '@jutro/legacy/locale';

function WMICPAPolicyRenewalPolicyDetailsPage(props) {
    const { wizardData: renewalVM, updateWizardData } = props;

    const { RenewalService } = useDependencies('RenewalService');
    const { setWizardLoading } = useWizardModals();
    const { authHeader, authUserData } = useAuthentication();
    const [showErrors, setShowErrors] = useState(false);
    const rateAsOfDate = _.get(renewalVM, 'baseData.rateAsOfDate.value');
    const [isInitialized, setInitialized] = useState(WMICConstantsUtil.alreadyInitialized(rateAsOfDate));
    const translator = useContext(TranslatorContext);

    const {
        onValidate: setComponentValidation,
        isComponentValid,
        initialValidation,
    } = useValidation('WMICPCPAPolicyDetailsPage');

    useDocumentTitle(translator(commonMessages.policyDetailsTitle), renewalVM);

    useEffect(() => {
        const initializeConstants = async () => {
            try {
                const constantsInitialized = await WMICConstantsUtil.initialize(rateAsOfDate, authHeader);

                setInitialized(constantsInitialized);
            } catch (error) {
                WMICLogger.error('Constants util initialization failed', error);
            }
        };

        !isInitialized && initializeConstants();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const canEditRenewal = useMemo(
        () => WMICUserAccessUtil.canEditRenewal(authUserData.permission_Ext),
        [authUserData.permission_Ext]
    );

    const onNext = useCallback(async () => {
        if (!isComponentValid) {
            setShowErrors(true);
            return false;
        }
        try {
            setWizardLoading(true);

            const newRenewalVM = _.cloneDeep(renewalVM.value);

            renewalVM.value = await RenewalService.saveRenewal([newRenewalVM], authHeader);
            updateWizardData(renewalVM);

            return renewalVM;
        } finally {
            setWizardLoading(false);
        }
        // Cannot include updateWizardData in the dependency array since calling it causes a new function to be created, which would cause in infinite loop in the useEffect below
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isComponentValid, setWizardLoading, renewalVM, RenewalService, authHeader]);

    if (!isInitialized) {
        return null;
    }

    return (
        <WMICWizardChangeOrRenewalPage
            showPrevious={false}
            onNext={onNext}
            cancelLabel={translator(commonMessages.saveAndExit)}
            isSkipping={initialValidation}
            showRequired
        >
            <WMICPolicyDetailsComponent
                jobVM={renewalVM}
                updateWizardData={updateWizardData}
                onValidate={setComponentValidation}
                isReadOnly={!canEditRenewal}
                authHeader={authHeader}
                showErrors={showErrors}
            />
        </WMICWizardChangeOrRenewalPage>
    );
}

WMICPAPolicyRenewalPolicyDetailsPage.propTypes = wizardProps;

export default withViewModelService(WMICPAPolicyRenewalPolicyDetailsPage);
